import React, {
  useRef, useState, useEffect, useContext, useCallback,
} from 'react';
import { string, shape } from 'prop-types';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { withLayoutGroup } from '@thd-olt-component-react/layout-manager';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import { Button } from '@thd-olt-component-react/core-ui';

import { isEventBusCreated, hasValidPipData, isExperienceDataResolved } from '../../utils/helpers';
import { makeApiRequest } from '../../utils/requestHelpers';
import { initImpressionWatchers } from '../../utils/initImpressionWatchers';
import useMVCSID from '../../hooks/useMVCSID';
import './SponsoredProductListing.scss';

const HYDRATION_STANDARD_SCROLL_BUFFER = 750;
const plaPrefix = 'sponsored-pip-pla';

const SponsoredProductListing = (props) => {
  const [adProducts, setAdProducts] = useState([]);
  const [onClickUrl, setOnClickUrl] = useState('');
  const experienceContext = useContext(ExperienceContext);
  const plaChannel = experienceContext.channel;
  let { pageContext } = props;
  const schema = 'pip_sponsored';
  const [fbtSkus, setFbtSkus] = useState([]);
  const ref = useRef(fbtSkus);
  const browserId = useMVCSID();
  const { storeId, isLocalized } = useStore({
    varnish: true,
    online: true
  });
  const plaRef = useRef();

  useEffect(() => {
    initImpressionWatchers();
  }, []);

  useEffect(() => {
    ref.current = fbtSkus;
  }, [fbtSkus]);

  useEffect(() => {
    if (isEventBusCreated()) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.on('frequently-bought-together.ready', (data) => {
        if (data?.output?.products) {
          const skus = data.output.products
            .map((product) => product.itemId)
            .filter((sku) => sku !== pageContext.data.itemId)
            .sort();
          if (ref.current.length === 0) {
            setFbtSkus(skus);
          }
        }
      });
    }
  }, [pageContext.data.itemId]);

  useEffect(() => {
    if (plaRef.current) {
      plaRef.current.setAttribute('onview-beacon-nucleus', adProducts[0].onViewBeacon.replace('https:', ''));
    }
  }, [plaRef.current]);

  const handleProductClick = useCallback((evt) => {
    evt.preventDefault();
    window.location = onClickUrl;
  }, [onClickUrl]);

  useEffect(() => {
    const isResolvedContext = isExperienceDataResolved({ isLocalized, experienceContext });
    if (hasValidPipData(pageContext, fbtSkus) && isResolvedContext && browserId) {
      makeApiRequest({
        browserId,
        schema,
        experienceContext,
        pageContext,
        setAdProducts,
        carouselType: 'pla',
        eventBusData: { fbtSkus }
      });
    }
  }, [fbtSkus, isLocalized, experienceContext.mounted, browserId]);

  useEffect(() => {
    if (adProducts.length > 0) {
      const product = adProducts[0];
      const beacon = product.onClickBeacon;
      let productPage = 'https://www.homedepot.com' + product.canonicalURL;
      productPage += (productPage.indexOf('?') > -1) ? '&' : '?';
      productPage += `ITC=AUC-${product.campaignId}-${product.placementId}-${product.slotId}`;
      if (beacon) {
        setOnClickUrl(beacon + productPage);
      } else {
        setOnClickUrl(productPage);
      }
    }
  }, [adProducts]);

  return (
    <>
      { adProducts.length > 0
        && (
          <div
            className={`${plaPrefix}-container ${plaChannel}`}
            data-onview-beacon-nucleus={adProducts[0].onViewBeacon.replace('https:', '')}
            ref={plaRef}
          >
            <div
              className={`${plaPrefix}-content ${plaChannel}`}
              data-testid="sponsored-product-listing"
            >
              <div className={`${plaPrefix}-pod ${plaChannel}`}>
                <QueryProvider
                  cacheKey="pla-on-pip"
                  defaultVariables={{}}
                >
                  <ProductPod
                    itemId={adProducts[0].id.toString()}
                    storeId={storeId}
                    onClick={handleProductClick}
                    mobilePodType="doubleColumn"
                    channelFromTest="mobile"
                    podType="mini"
                    hideFavorites
                    hideFulfillment
                    hideBadges
                    hideLimitPerOrder
                    hideRebates
                    hideSavingsText
                    hideSeeSimilarItems
                    hideShippingInfo
                    hideShopThisCollection
                    hideStoreInfo
                    doNotShowChooseYourOptions
                    miniOptions={{
                      enableATC: false,
                      onlyShowIfFulfillable: false,
                      disabled: true,
                      ctaLocation: 'right'
                    }}
                  />
                </QueryProvider>
                <div className={`${plaPrefix}-shop-now-wrapper ${plaChannel}`}>
                  <Button
                    className={`${plaPrefix}-shop-now-btn ${plaChannel}`}
                    outline
                    onClick={() => { window.location = onClickUrl; }}
                  >
                    Shop Now
                  </Button>
                </div>
              </div>
              <p className={`${plaPrefix}-sponsored-tag ${plaChannel}`}>Sponsored</p>
            </div>
          </div>
        )}
    </>
  );
};

const propTypes = {
  pageContext: shape({
    label: string,
    data: shape({
      itemId: Number
    })
  })
};

const defaultProps = {
  pageContext: {}
};

SponsoredProductListing.propTypes = propTypes;
SponsoredProductListing.defaultProps = defaultProps;

SponsoredProductListing.dataModel = extend({}, ProductPod);

const HydratedSponsoredProductListing = withHydrator({
  id: `hydrated-${plaPrefix}`,
  className: `${plaPrefix}-hydrator`,
  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
}, SponsoredProductListing);

const DynamicSponsoredProductListing = withDynamicComponent(HydratedSponsoredProductListing);
const SponsoredProductListingAd = withLayoutGroup({ name: `${plaPrefix}` }, DynamicSponsoredProductListing);

SponsoredProductListingAd.propTypes = propTypes;
SponsoredProductListingAd.defaultProps = defaultProps;

export { SponsoredProductListing, SponsoredProductListingAd };
